import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Dropdown from '@/components/ui/Dropdown';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import chevron from '@/assets/icons/chevron.svg';
import icFilterExpander from '@/assets/icons/filterExpander.svg';
import filterStyles from '@/components/filters/OptionsRangeFilter/OptionsRangeFilter.module.css';
import fundStyles from '@/components/FundSelect/fundSelect.module.css';

interface BeneficiaryOption {
  id: number;
  firstName: string;
  lastName: string;
  israeliId: string;
}

interface BeneficiaryFilterProps {
  selectedBeneficiaryContactId: number | null;
  onBeneficiaryChange: (id: number | null) => void;
}

const BeneficiaryFilter = ({ selectedBeneficiaryContactId, onBeneficiaryChange }: BeneficiaryFilterProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
  const listContainerRef = useRef<HTMLDivElement>(null);

  const { data: allBeneficiaries, isLoading } = useGetAuthApi<BeneficiaryOption[]>('management/supporteds-basic');

  const filteredBeneficiaries = (allBeneficiaries ?? []).filter((b) =>
    `${b.israeliId} ${b.firstName} ${b.lastName}`.toLowerCase().includes(searchValue.toLowerCase())
  );

  const virtualizer = useVirtualizer({
    count: filteredBeneficiaries.length,
    getScrollElement: () => listContainerRef.current,
    estimateSize: () => 40,
    overscan: 5,
  });

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSetOpen = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSearchValue('');
  };

  const handleSelect = (beneficiary: BeneficiaryOption) => {
    onBeneficiaryChange(beneficiary.id);
    setIsOpen(false);
    setSearchValue('');
  };

  const handleClear = () => {
    onBeneficiaryChange(null);
    setIsOpen(false);
    setSearchValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredBeneficiaries.length > 0) {
      handleSelect(filteredBeneficiaries[0]);
    }
  };

  const hasValue = selectedBeneficiaryContactId != null;
  const selectedBeneficiary = selectedBeneficiaryContactId != null
    ? (allBeneficiaries ?? []).find((b) => b.id === selectedBeneficiaryContactId) ?? null
    : null;
  const triggerLabel = selectedBeneficiary
    ? `${selectedBeneficiary.israeliId} | ${selectedBeneficiary.firstName} ${selectedBeneficiary.lastName}`
    : t('supported');

  return (
    <Dropdown
      trigger={
        <Button
          className={`
            ${filterStyles.filterButton}
            ${isOpen ? filterStyles.filterButtonDropdownOpen : ''}
            ${hasValue ? filterStyles.filterButtonHasValue : ''}
          `}
        >
          <span className="body-md-regular text-(--font-primary)">{triggerLabel}</span>
          <img src={icFilterExpander} alt="expander" />
        </Button>
      }
      contentAlign="start"
      isOpen={isOpen}
      setIsOpen={handleSetOpen}
    >
      <div className={fundStyles['select-menu']}>
        <SearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder={t('searchSupported')}
          className={fundStyles['search-container']}
          inputRef={searchInputRef}
          onKeyDown={handleKeyDown}
        />
        <div className={fundStyles['options-container']}>
          {hasValue && (
            <button className={fundStyles.option} onClick={handleClear}>
              <span className="body-md-regular">{t('allBeneficiaries')}</span>
            </button>
          )}
          {isLoading ? (
            <p className={`${fundStyles.option} body-sm-regular`}>{t('loading')}...</p>
          ) : filteredBeneficiaries.length === 0 ? (
            <p className={`${fundStyles.option} body-sm-regular`}>{t('noBeneficiariesFound')}</p>
          ) : (
            <div
              ref={listContainerRef}
              className={fundStyles['virtual-container']}
              style={{ height: Math.min(filteredBeneficiaries.length * 40, 220) }}
            >
              <div
                className={fundStyles['virtual-content']}
                style={{ height: `${virtualizer.getTotalSize()}px` }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const beneficiary = filteredBeneficiaries[virtualRow.index];
                  return (
                    <div
                      key={virtualRow.key}
                      ref={virtualizer.measureElement}
                      data-index={virtualRow.index}
                      className={fundStyles['virtual-item']}
                      style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                      <button
                        className={`${fundStyles.option} ${beneficiary.id === selectedBeneficiaryContactId ? 'bg-(--surface-secondary-lowest)' : ''}`}
                        onClick={() => handleSelect(beneficiary)}
                      >
                        <span className="body-md-semibold">{beneficiary.israeliId}</span>
                        <div className={fundStyles.divider} />
                        <span className="body-sm-regular">{beneficiary.firstName} {beneficiary.lastName}</span>
                        <span className="ms-auto">
                          <img src={chevron} alt="" className={fundStyles.chevron} />
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </Dropdown>
  );
};

export default BeneficiaryFilter;
